import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, LayoutDashboard, Users, Target, Kanban, ListChecks,
  CheckSquare, ShieldAlert, History, Search, Plus, ArrowRight,
  Sparkles, Phone, Mail, MessageSquare, Calendar, Building2,
  Merge, ExternalLink, TrendingUp, AlertCircle, Info, Inbox,
  Route, Send, Trash2, X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  lccBootstrap, lccBackfill, lccCommandCenter, lccContacts, lccContact,
  lccCreateContact, lccMergeContacts, lccContactDuplicates,
  lccLeads, lccCreateLead, lccUpdateLead, lccRescoreLead,
  lccOpportunities, lccCreateOpp, lccUpdateOpp,
  lccTasks, lccCreateTask, lccUpdateTask,
  lccApprovals, lccDecideApproval, lccAudit,
  lccCompanies, lccCompany, lccCreateCompany,
  lccRoutingRules, lccCreateRoutingRule, lccUpdateRoutingRule, lccDeleteRoutingRule, lccTestRouting,
  lccInboxThreads, lccInboxThread, lccInboxReply,
} from "@/lib/api";

const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
const STAGE_COLORS = {
  new: "bg-slate-100 text-slate-700 border-slate-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-indigo-50 text-indigo-700 border-indigo-200",
  proposal: "bg-amber-50 text-amber-700 border-amber-200",
  negotiation: "bg-orange-50 text-orange-700 border-orange-200",
  won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-rose-50 text-rose-700 border-rose-200",
};
const KIND_ICONS = {
  form: MessageSquare, chat: MessageSquare, voice: Phone, call: Phone,
  sms: MessageSquare, whatsapp: MessageSquare, email: Mail, note: MessageSquare,
  task: CheckSquare, meeting: Calendar, appointment: Calendar,
  quote: Target, payment: TrendingUp, stage_change: ArrowRight,
  ai_summary: Sparkles, ai_action: Sparkles, system: Info, assignment: Users,
};

const scoreColor = (v) => (v >= 70 ? "text-emerald-600" : v >= 40 ? "text-amber-600" : "text-slate-500");
const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtRelative = (s) => {
  if (!s) return "—";
  const diff = (Date.now() - new Date(s).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function LeadCustomerCenter() {
  const { user } = useAuth();
  const [tab, setTab] = useState("command");
  const [boot, setBoot] = useState(null);
  const [bootErr, setBootErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    lccBootstrap().then(setBoot).catch((e) => setBootErr(e?.response?.data?.detail || "Failed to load"));
  }, [user]);

  if (user === undefined) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (user === null) {
    return (
      <div className="min-h-screen flex flex-col"><Navbar />
        <div className="flex-1 grid place-items-center px-6">
          <div className="max-w-md text-center" data-testid="lcc-login-gate">
            <LayoutDashboard className="w-10 h-10 text-indigo-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Lead &amp; Customer Center</h1>
            <p className="text-slate-600 mb-6">One shared customer memory — sign in to see every form, chat, call and booking as a single record.</p>
            <Link to="/login" data-testid="lcc-login-link" className="inline-block px-6 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700">Sign in to continue</Link>
          </div>
        </div><Footer />
      </div>
    );
  }

  const tabs = [
    ["command", "Command Center", LayoutDashboard],
    ["inbox", "Inbox", Inbox],
    ["leads", "Leads", Target],
    ["contacts", "Contacts", Users],
    ["companies", "Companies", Building2],
    ["pipeline", "Pipeline", Kanban],
    ["tasks", "Tasks", CheckSquare],
    ["approvals", "AI Approvals", ShieldAlert],
    ["routing", "Routing", Route],
    ["audit", "Audit Log", History],
  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50"><Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 pt-28 pb-12" data-testid="lcc-page">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Lead &amp; Customer Center</h1>
            <p className="text-slate-600 mt-1 text-sm">One shared memory — every form, chat, call and booking, joined by contact.</p>
          </div>
          <BackfillButton onDone={() => { lccBootstrap().then(setBoot); }} />
        </div>
        {bootErr && <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm" data-testid="lcc-boot-error"><AlertCircle className="w-4 h-4 inline mr-1" /> {bootErr}</div>}
        <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
          {tabs.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`lcc-tab-${k}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === k ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>
        {tab === "command" && <CommandCenterTab />}
        {tab === "inbox" && <InboxTab />}
        {tab === "leads" && <LeadsTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "companies" && <CompaniesTab />}
        {tab === "pipeline" && <PipelineTab />}
        {tab === "tasks" && <TasksTab />}
        {tab === "approvals" && <ApprovalsTab />}
        {tab === "routing" && <RoutingTab />}
        {tab === "audit" && <AuditTab />}
      </main><Footer />
    </div>
  );
}

function BackfillButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      const r = await lccBackfill();
      setRes(r);
      onDone?.();
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      {res && (
        <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1" data-testid="lcc-backfill-result">
          Imported {res.imported.contacts} contacts, {res.imported.leads} leads, {res.imported.activities} activities
        </span>
      )}
      <button onClick={run} disabled={busy} data-testid="lcc-backfill-btn"
        className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
        Sync from existing sources
      </button>
    </div>
  );
}

// ============================================================================
// Command Center
// ============================================================================
function CommandCenterTab() {
  const [d, setD] = useState(null);
  useEffect(() => { lccCommandCenter().then(setD).catch(() => setD({ error: true })); }, []);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (d.error) return <div className="py-8 text-slate-500 text-center">Unable to load.</div>;
  const tiles = [
    ["New leads", d.tiles.new_leads, Target, "text-indigo-600"],
    ["Hot leads (score 60+)", d.tiles.hot_leads, TrendingUp, "text-orange-600"],
    ["Open opportunities", d.tiles.open_opportunities, Kanban, "text-emerald-600"],
    ["Tasks due", d.tiles.tasks_due, CheckSquare, "text-rose-600"],
    ["Pending approvals", d.tiles.pending_approvals, ShieldAlert, "text-amber-600"],
  ];
  return (
    <div className="space-y-6" data-testid="lcc-command-center">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tiles.map(([l, v, Icon, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4">
            <Icon className={`w-4 h-4 ${c} mb-2`} />
            <div className="text-2xl font-bold text-slate-900" data-testid={`tile-${l.split(" ")[0].toLowerCase()}`}>{v}</div>
            <div className="text-xs text-slate-500 mt-0.5">{l}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-indigo-600" /> Priority leads</h2>
          {d.priority_leads?.length ? (
            <div className="space-y-2" data-testid="priority-leads">
              {d.priority_leads.map((l) => (
                <div key={l.id} className="rounded-lg border border-slate-200 p-3 hover:border-indigo-300 transition-colors" data-testid={`priority-lead-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{l.contact?.name || "Unknown"}</div>
                      <div className="text-xs text-slate-500 truncate">{l.source} · {l.requested_service || "no service"}</div>
                      {l.ai_summary && <div className="text-xs text-slate-600 mt-1 italic">"{l.ai_summary}"</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-lg font-bold ${scoreColor(l.score?.overall || 0)}`}>{l.score?.overall || 0}</div>
                      <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border ${STAGE_COLORS[l.stage] || "bg-slate-100 text-slate-500"}`}>{l.stage}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="text-sm text-slate-500 py-6 text-center">No hot leads right now.</div>}
        </section>
        <section className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><History className="w-4 h-4 text-slate-600" /> Recent activity</h2>
          <ActivityStream items={d.recent_activity || []} />
        </section>
      </div>
    </div>
  );
}

function ActivityStream({ items, dense = false }) {
  if (!items?.length) return <div className="text-sm text-slate-500 py-6 text-center">No activity yet.</div>;
  return (
    <ol className="space-y-2" data-testid="activity-stream">
      {items.map((a) => {
        const Icon = KIND_ICONS[a.kind] || Info;
        return (
          <li key={a.id} className={`flex gap-3 ${dense ? "" : "p-2 rounded-lg hover:bg-slate-50"}`} data-testid={`activity-${a.id}`}>
            <div className="shrink-0 w-7 h-7 rounded-full bg-indigo-50 grid place-items-center"><Icon className="w-3.5 h-3.5 text-indigo-600" /></div>
            <div className="min-w-0 flex-1">
              <div className="text-xs text-slate-500">{fmtRelative(a.at)} · <span className="capitalize">{a.kind}</span>{a.channel ? ` · ${a.channel}` : ""}{a.is_ai_inference && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-violet-50 text-violet-700">AI</span>}</div>
              <div className="text-sm text-slate-800 truncate">{a.title || a.body}</div>
              {a.body && a.title && <div className="text-xs text-slate-500 line-clamp-2">{a.body}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// ============================================================================
// Contacts
// ============================================================================
function ContactsTab() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => {
    lccContacts(q ? { q } : {}).then((r) => setRows(r.contacts || [])).catch(() => setRows([]));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4" data-testid="lcc-contacts">
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input className={field + " pl-9"} placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contacts-search" />
          </div>
          <button onClick={() => setShowNew(true)} data-testid="contacts-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New</button>
        </div>
        {rows === null ? <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> :
          rows.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-200 bg-white">No contacts yet. Click "Sync from existing sources" above.</div> :
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {rows.map((c) => (
              <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`contact-row-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 flex items-center gap-3 ${selected === c.id ? "bg-indigo-50" : ""}`}>
                <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 grid place-items-center text-sm font-semibold">{(c.name || "?").charAt(0).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{c.name || "Unknown"}</div>
                  <div className="text-xs text-slate-500 truncate">{(c.emails || [])[0] || (c.phones || [])[0] || "—"}</div>
                </div>
                <div className="text-[10px] text-slate-400 shrink-0">{fmtRelative(c.last_seen)}</div>
              </button>
            ))}
          </div>}
      </div>
      <div>
        {selected ? <ContactDetail cid={selected} onChange={load} /> : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white h-full min-h-[400px] grid place-items-center text-slate-400 text-sm" data-testid="contact-empty">Select a contact to see their full timeline.</div>
        )}
      </div>
      {showNew && <NewContactModal onClose={() => setShowNew(false)} onSaved={(c) => { setShowNew(false); setSelected(c.id); load(); }} />}
    </div>
  );
}

function NewContactModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true); setErr("");
    try { const c = await lccCreateContact(f); onSaved(c); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="new-contact-modal">
        <h3 className="text-lg font-bold text-slate-900">New contact</h3>
        <input className={field} placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="nc-name" />
        <input className={field} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="nc-email" />
        <input className={field} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="nc-phone" />
        <input className={field} placeholder="Company (optional)" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="nc-company" />
        {err && <div className="text-xs text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="nc-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

function ContactDetail({ cid, onChange }) {
  const [d, setD] = useState(null);
  const [dupes, setDupes] = useState([]);
  const load = useCallback(() => {
    lccContact(cid).then(setD).catch(() => setD(null));
    lccContactDuplicates(cid).then((r) => setDupes(r.duplicates || [])).catch(() => setDupes([]));
  }, [cid]);
  useEffect(() => { load(); }, [load]);
  const merge = async (mergeId) => {
    if (!window.confirm("Merge this record into the current one? This cannot be undone.")) return;
    await lccMergeContacts({ keep_id: cid, merge_id: mergeId });
    load(); onChange?.();
  };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const c = d.contact;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`contact-detail-${cid}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900" data-testid="cd-name">{c.name}</h2>
          <div className="text-sm text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            {(c.emails || []).map((e) => <span key={e} className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {e}</span>)}
            {(c.phones || []).map((p) => <span key={p} className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {p}</span>)}
            {c.company && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {c.company}</span>}
          </div>
          <div className="text-xs text-slate-400 mt-1">First seen {fmtRelative(c.first_seen)} · Last seen {fmtRelative(c.last_seen)} · Source: {c.source || "unknown"}</div>
        </div>
        {c.do_not_contact && <span className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Do not contact</span>}
      </div>
      {dupes.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="cd-duplicates">
          <div className="text-xs font-semibold text-amber-800 mb-1.5 flex items-center gap-1"><Merge className="w-3.5 h-3.5" /> Possible duplicates found ({dupes.length})</div>
          <div className="space-y-1.5">
            {dupes.map((d2) => (
              <div key={d2.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate"><strong>{d2.name}</strong> — {(d2.emails || [])[0] || (d2.phones || [])[0]}</span>
                <button onClick={() => merge(d2.id)} data-testid={`cd-merge-${d2.id}`} className="px-2 py-1 rounded bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700">Merge into this</button>
              </div>
            ))}
          </div>
        </div>
      )}
      {d.leads?.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Leads ({d.leads.length})</div>
          <div className="space-y-1.5">
            {d.leads.slice(0, 5).map((l) => (
              <div key={l.id} className="flex items-center justify-between text-xs rounded-lg border border-slate-200 px-3 py-2" data-testid={`cd-lead-${l.id}`}>
                <span className="truncate"><strong>{l.source}</strong> · {l.requested_service || "no service"} {l.ai_summary && <span className="text-slate-500 italic">— "{l.ai_summary.slice(0, 80)}"</span>}</span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-bold ${scoreColor(l.score?.overall || 0)}`}>{l.score?.overall || 0}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STAGE_COLORS[l.stage]}`}>{l.stage}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Timeline ({d.activities.length})</div>
        <ActivityStream items={d.activities} />
      </div>
    </div>
  );
}

// ============================================================================
// Leads
// ============================================================================
function LeadsTab() {
  const [d, setD] = useState(null);
  const [stage, setStage] = useState("");
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => {
    lccLeads({ ...(stage ? { stage } : {}), ...(q ? { q } : {}) }).then(setD).catch(() => setD({ leads: [], stages: [], pipeline: {} }));
  }, [stage, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="lcc-leads">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input className={field + " pl-9"} placeholder="Search leads…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-search" />
        </div>
        <select className={field + " max-w-[180px]"} value={stage} onChange={(e) => setStage(e.target.value)} data-testid="leads-stage-filter">
          <option value="">All stages</option>
          {d.stages.map((s) => <option key={s.id} value={s.id}>{s.name} ({d.pipeline[s.id] || 0})</option>)}
        </select>
        <button onClick={() => setShowNew(true)} data-testid="leads-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New lead</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-7 gap-2">
        {d.stages.map((s) => (
          <div key={s.id} className={`rounded-lg border p-3 ${STAGE_COLORS[s.id]}`} data-testid={`pipe-${s.id}`}>
            <div className="text-xl font-bold">{d.pipeline[s.id] || 0}</div>
            <div className="text-[11px] font-medium">{s.name}</div>
          </div>
        ))}
      </div>
      {d.leads.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No leads match this filter.</div> :
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {d.leads.map((l) => <LeadRow key={l.id} lead={l} stages={d.stages} onChange={load} />)}
        </div>}
      {showNew && <NewLeadModal stages={d.stages} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function LeadRow({ lead, stages, onChange }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const change = async (stageId) => {
    setBusy(true);
    try { await lccUpdateLead(lead.id, { stage: stageId }); onChange?.(); }
    finally { setBusy(false); }
  };
  const rescore = async () => {
    setBusy(true);
    try { await lccRescoreLead(lead.id); onChange?.(); }
    finally { setBusy(false); }
  };
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`lead-row-${lead.id}`}>
      <div className="px-4 py-3 flex items-center gap-3">
        <button onClick={() => setOpen((o) => !o)} className="min-w-0 flex-1 text-left">
          <div className="text-sm font-semibold text-slate-900 truncate">{lead.contact?.name || "Unknown"} <span className="text-slate-400 font-normal">· {lead.source}</span></div>
          <div className="text-xs text-slate-500 truncate">{lead.requested_service || "—"}{lead.ai_summary ? ` · ${lead.ai_summary.slice(0, 100)}` : ""}</div>
        </button>
        <div className={`text-lg font-bold ${scoreColor(lead.score?.overall || 0)}`}>{lead.score?.overall || 0}</div>
        <select disabled={busy} value={lead.stage} onChange={(e) => change(e.target.value)} data-testid={`lead-stage-${lead.id}`}
          className={`text-xs rounded border px-2 py-1 ${STAGE_COLORS[lead.stage]}`}>
          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button onClick={rescore} disabled={busy} title="Re-score & re-summarize" data-testid={`lead-rescore-${lead.id}`}
          className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Rescore</button>
      </div>
      {open && (
        <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100 text-xs text-slate-700 space-y-2" data-testid={`lead-detail-${lead.id}`}>
          <div><strong>Message:</strong> {lead.message || "—"}</div>
          {lead.ai_summary && <div className="rounded bg-white border border-slate-200 p-2"><Sparkles className="w-3 h-3 inline text-violet-500 mr-1" /><strong>AI summary:</strong> {lead.ai_summary}</div>}
          {lead.score?.breakdown && Object.keys(lead.score.breakdown).length > 0 && (
            <div><strong>Score breakdown:</strong> {Object.entries(lead.score.breakdown).map(([k, v]) => <span key={k} className="inline-block bg-white border border-slate-200 rounded px-1.5 py-0.5 mr-1 mt-1">{k}: +{v}</span>)}</div>
          )}
          {lead.external_refs?.length > 0 && (
            <div className="text-slate-500"><ExternalLink className="w-3 h-3 inline mr-1" />Sourced from: {lead.external_refs.map((r) => `${r.collection}/${r.id?.slice(0, 8)}`).join(", ")}</div>
          )}
        </div>
      )}
    </div>
  );
}

function NewLeadModal({ stages, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", source: "manual", requested_service: "", message: "", urgency: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true); setErr("");
    try { await lccCreateLead(f); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="new-lead-modal">
        <h3 className="text-lg font-bold text-slate-900">New lead</h3>
        <input className={field} placeholder="Contact name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="nl-name" />
        <div className="grid grid-cols-2 gap-2">
          <input className={field} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="nl-email" />
          <input className={field} placeholder="Phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="nl-phone" />
        </div>
        <input className={field} placeholder="Requested service" value={f.requested_service} onChange={(e) => setF({ ...f, requested_service: e.target.value })} data-testid="nl-service" />
        <select className={field} value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} data-testid="nl-urgency">
          <option value="">Urgency: not specified</option><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
        </select>
        <textarea rows={3} className={field} placeholder="Message / notes" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="nl-message" />
        {err && <div className="text-xs text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="nl-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Pipeline (Kanban view of opportunities)
// ============================================================================
function PipelineTab() {
  const [d, setD] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => { lccOpportunities().then(setD).catch(() => setD({ opportunities: [], stages: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  const move = async (oid, stageId) => { await lccUpdateOpp(oid, { stage: stageId }); load(); };
  const byStage = useMemo(() => {
    const m = {};
    (d?.stages || []).forEach((s) => { m[s.id] = []; });
    (d?.opportunities || []).forEach((o) => { (m[o.stage] = m[o.stage] || []).push(o); });
    return m;
  }, [d]);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="lcc-pipeline">
      <div className="flex justify-end">
        <button onClick={() => setShowNew(true)} data-testid="pipe-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New opportunity</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 overflow-x-auto">
        {d.stages.map((s) => (
          <div key={s.id} className={`rounded-xl border p-2 min-h-[200px] ${STAGE_COLORS[s.id]}`} data-testid={`pipe-col-${s.id}`}>
            <div className="text-xs font-semibold px-1 pb-2 flex items-center justify-between">
              <span>{s.name}</span>
              <span className="opacity-60">{(byStage[s.id] || []).length}</span>
            </div>
            <div className="space-y-2">
              {(byStage[s.id] || []).map((o) => (
                <div key={o.id} className="rounded-lg bg-white border border-slate-200 p-2 shadow-sm" data-testid={`opp-${o.id}`}>
                  <div className="text-xs font-semibold text-slate-900 truncate">{o.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{o.contact?.name || "—"}</div>
                  <div className="text-xs text-slate-700 mt-1">{o.currency} {(o.value || 0).toLocaleString()}</div>
                  <select value={o.stage} onChange={(e) => move(o.id, e.target.value)} data-testid={`opp-stage-${o.id}`}
                    className="mt-1 w-full text-[10px] rounded border border-slate-200 px-1 py-0.5">
                    {d.stages.map((s2) => <option key={s2.id} value={s2.id}>{s2.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {showNew && <NewOppModal stages={d.stages} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewOppModal({ stages, onClose, onSaved }) {
  const [contacts, setContacts] = useState([]);
  const [f, setF] = useState({ contact_id: "", name: "", value: 0, currency: "USD", stage: "new", probability: 20 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { lccContacts({ limit: 200 }).then((r) => setContacts(r.contacts || [])).catch(() => {}); }, []);
  const save = async () => {
    if (!f.contact_id || !f.name) { setErr("Contact and name are required"); return; }
    setBusy(true); setErr("");
    try { await lccCreateOpp({ ...f, value: Number(f.value) || 0, probability: Number(f.probability) || 20 }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="new-opp-modal">
        <h3 className="text-lg font-bold text-slate-900">New opportunity</h3>
        <select className={field} value={f.contact_id} onChange={(e) => setF({ ...f, contact_id: e.target.value })} data-testid="no-contact">
          <option value="">Choose a contact</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.name} · {(c.emails || [])[0] || (c.phones || [])[0] || ""}</option>)}
        </select>
        <input className={field} placeholder="Opportunity name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="no-name" />
        <div className="grid grid-cols-3 gap-2">
          <input type="number" className={field} placeholder="Value" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="no-value" />
          <input className={field} placeholder="USD" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} data-testid="no-currency" />
          <input type="number" className={field} placeholder="Prob %" value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} data-testid="no-prob" />
        </div>
        <select className={field} value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} data-testid="no-stage">
          {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {err && <div className="text-xs text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="no-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Tasks
// ============================================================================
function TasksTab() {
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => { lccTasks().then((r) => setRows(r.tasks || [])).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (t) => { await lccUpdateTask(t.id, { status: t.status === "done" ? "open" : "done" }); load(); };
  if (rows === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-3" data-testid="lcc-tasks">
      <div className="flex justify-end">
        <button onClick={() => setShowNew(true)} data-testid="tasks-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New task</button>
      </div>
      {rows.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No tasks. Create one or let AI generate them from leads.</div> :
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {rows.map((t) => (
            <div key={t.id} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center gap-3" data-testid={`task-row-${t.id}`}>
              <input type="checkbox" checked={t.status === "done"} onChange={() => toggle(t)} data-testid={`task-toggle-${t.id}`} className="w-4 h-4" />
              <div className="flex-1">
                <div className={`text-sm ${t.status === "done" ? "line-through text-slate-400" : "text-slate-900 font-medium"}`}>{t.title}</div>
                <div className="text-xs text-slate-500">Due {fmtDate(t.due_at)} · {t.owner_id || "unassigned"} · {t.source}</div>
              </div>
            </div>
          ))}
        </div>}
      {showNew && <NewTaskModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewTaskModal({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", due_at: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return;
    setBusy(true);
    try { await lccCreateTask(f); onSaved(); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="new-task-modal">
        <h3 className="text-lg font-bold text-slate-900">New task</h3>
        <input className={field} placeholder="Task title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="nt-title" />
        <input type="datetime-local" className={field} value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} data-testid="nt-due" />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="nt-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Approvals — AI-drafted actions awaiting human review
// ============================================================================
function ApprovalsTab() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { lccApprovals().then((r) => setRows(r.approvals || [])).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const decide = async (aid, decision) => { await lccDecideApproval(aid, { decision }); load(); };
  if (rows === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (rows.length === 0) return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center" data-testid="lcc-approvals-empty">
      <ShieldAlert className="w-8 h-8 mx-auto text-slate-300 mb-2" />
      <p className="text-sm text-slate-500">No pending approvals.</p>
      <p className="text-xs text-slate-400 mt-1">When AI drafts a first-touch reply for a new lead, it appears here for one-click approval.</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="lcc-approvals">
      <div className="text-xs text-slate-500 flex items-center gap-1"><Sparkles className="w-3 h-3 text-violet-500" /> AI drafts a first-touch reply for every new lead and drops it here. Approve to send, reject to discard.</div>
      {rows.map((a) => {
        const pa = a.proposed_action || {};
        const ev = a.evidence || {};
        const ChIcon = KIND_ICONS[pa.channel] || Sparkles;
        return (
          <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`approval-${a.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><ChIcon className="w-4 h-4 text-violet-600" /> {a.kind.replace(/_/g, " ")} <span className="text-slate-400 font-normal">· {pa.channel}</span></div>
                <div className="text-xs text-slate-500 mt-0.5">To <span className="font-mono">{pa.to || "—"}</span> · Score {ev.score?.overall || 0} · {fmtRelative(a.created_at)}</div>
                {ev.ai_summary && <div className="text-xs text-slate-500 italic mt-1">"{ev.ai_summary}"</div>}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid={`approval-body-${a.id}`}>{pa.body}</div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => decide(a.id, "approve")} data-testid={`approve-${a.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"><Send className="w-3 h-3" /> Approve & send</button>
              <button onClick={() => decide(a.id, "reject")} data-testid={`reject-${a.id}`} className="px-3 py-1.5 rounded bg-white border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50">Reject</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Unified Inbox — one thread per contact across all channels
// ============================================================================
function InboxTab() {
  const [threads, setThreads] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => { lccInboxThreads({ limit: 100 }).then((r) => setThreads(r.threads || [])).catch(() => setThreads([])); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[500px]" data-testid="lcc-inbox">
      <div className="rounded-2xl border border-slate-200 bg-white overflow-y-auto">
        {threads === null ? <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> :
          threads.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center px-4">No conversations yet. Every chat, SMS, WhatsApp, email or call across all channels will appear here as one thread per contact.</div> :
          threads.map((t) => {
            const KIcon = KIND_ICONS[t.last_kind] || MessageSquare;
            const isIn = t.last_direction === "in";
            return (
              <button key={t.contact_id} onClick={() => setSelected(t.contact_id)} data-testid={`inbox-thread-${t.contact_id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex gap-3 ${selected === t.contact_id ? "bg-indigo-50" : ""}`}>
                <div className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${isIn ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}><KIcon className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-900 truncate">{t.contact.name}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{fmtRelative(t.last_at)}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{isIn ? "" : "You: "}{t.last_body || t.last_title || t.last_kind}</div>
                  <div className="text-[10px] text-slate-400">{t.last_channel} · {t.message_count} msg</div>
                </div>
              </button>
            );
          })}
      </div>
      <div>
        {selected ? <InboxThread cid={selected} onSent={load} /> : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white h-full grid place-items-center text-slate-400 text-sm" data-testid="inbox-empty">Select a conversation to view the full thread.</div>
        )}
      </div>
    </div>
  );
}

function InboxThread({ cid, onSent }) {
  const [d, setD] = useState(null);
  const [channel, setChannel] = useState("email");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => { lccInboxThread(cid).then(setD).catch(() => setD(null)); }, [cid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.contact) {
      if ((d.contact.emails || []).length) setChannel("email");
      else if ((d.contact.phones || []).length) setChannel("sms");
      else setChannel("note");
    }
  }, [d?.contact]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true); setMsg("");
    try {
      const r = await lccInboxReply(cid, { channel, body: text });
      setText("");
      setMsg(r.send_result?.mocked ? "Sent via mock (Sent.dm key not set)" : "Sent ✓");
      load(); onSent?.();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white h-full flex flex-col" data-testid={`inbox-thread-detail-${cid}`}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">{d.contact.name}</div>
          <div className="text-xs text-slate-500">{(d.contact.emails || []).join(" · ") || "no email"} · {(d.contact.phones || []).join(" · ") || "no phone"}</div>
        </div>
        {d.contact.do_not_contact && <span className="text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">Do not contact</span>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {d.messages.length === 0 && <div className="text-sm text-slate-400 text-center py-8">No messages yet in this thread.</div>}
        {d.messages.map((m) => {
          const isOut = m.direction === "out";
          const KIcon = KIND_ICONS[m.kind] || MessageSquare;
          const isAI = m.actor_kind === "ai" || m.is_ai_inference;
          return (
            <div key={m.id} className={`flex ${isOut ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${isOut ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                <div className={`text-[10px] mb-0.5 flex items-center gap-1 ${isOut ? "text-indigo-200" : "text-slate-500"}`}>
                  <KIcon className="w-3 h-3" /> {m.channel || m.kind} · {fmtRelative(m.at)}
                  {isAI && <span className={`ml-1 px-1 rounded ${isOut ? "bg-indigo-800" : "bg-violet-100 text-violet-700"}`}>AI</span>}
                </div>
                {m.title && <div className="text-xs font-semibold mb-0.5">{m.title}</div>}
                <div className="text-sm whitespace-pre-wrap">{m.body || m.title}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t border-slate-100 p-3 space-y-2">
        <div className="flex gap-2">
          <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="inbox-channel"
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs">
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="note">Internal note</option>
          </select>
          {msg && <span className={`text-xs px-2 py-1 rounded ${msg.includes("Sent") ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`} data-testid="inbox-send-status">{msg}</span>}
        </div>
        <div className="flex gap-2">
          <textarea rows={2} className={field} placeholder={channel === "note" ? "Internal note (customer won't see)…" : `Reply via ${channel}…`}
            value={text} onChange={(e) => setText(e.target.value)} data-testid="inbox-reply-input" />
          <button onClick={send} disabled={busy || !text.trim()} data-testid="inbox-send-btn"
            className="shrink-0 self-end inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Companies
// ============================================================================
function CompaniesTab() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => {
    lccCompanies(q ? { q } : {}).then((r) => setRows(r.companies || [])).catch(() => setRows([]));
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4" data-testid="lcc-companies">
      <div className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input className={field + " pl-9"} placeholder="Search companies…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="companies-search" />
          </div>
          <button onClick={() => setShowNew(true)} data-testid="companies-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New</button>
        </div>
        {rows === null ? <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> :
          rows.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-200 bg-white px-4">No companies yet. Companies are auto-created from B2B contact email domains (e.g. acme.com), or you can add one manually.</div> :
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {rows.map((c) => (
              <button key={c.id} onClick={() => setSelected(c.id)} data-testid={`company-row-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 flex items-center gap-3 ${selected === c.id ? "bg-indigo-50" : ""}`}>
                <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center"><Building2 className="w-4 h-4 text-slate-600" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.domain || c.industry || "—"}</div>
                </div>
                <div className="text-[10px] text-slate-400 shrink-0 text-right">
                  <div>{c.contact_count} contacts</div>
                  <div className="text-emerald-600 font-medium">{c.open_lead_count} open</div>
                </div>
              </button>
            ))}
          </div>}
      </div>
      <div>
        {selected ? <CompanyDetail cid={selected} onChange={load} /> : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white h-full min-h-[400px] grid place-items-center text-slate-400 text-sm" data-testid="company-empty">Select a company to see its people, opportunities, and lifetime value.</div>
        )}
      </div>
      {showNew && <NewCompanyModal onClose={() => setShowNew(false)} onSaved={(c) => { setShowNew(false); setSelected(c.id); load(); }} />}
    </div>
  );
}

function NewCompanyModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", domain: "", industry: "", size: "", revenue: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim() && !f.domain.trim()) { setErr("Name or domain required"); return; }
    setBusy(true); setErr("");
    try { const c = await lccCreateCompany(f); onSaved(c); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="new-company-modal">
        <h3 className="text-lg font-bold text-slate-900">New company</h3>
        <input className={field} placeholder="Company name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="nco-name" />
        <input className={field} placeholder="Domain (e.g. acme.com)" value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} data-testid="nco-domain" />
        <input className={field} placeholder="Industry" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} data-testid="nco-industry" />
        <div className="grid grid-cols-2 gap-2">
          <input className={field} placeholder="Size (1-10, 11-50…)" value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} data-testid="nco-size" />
          <input className={field} placeholder="Revenue" value={f.revenue} onChange={(e) => setF({ ...f, revenue: e.target.value })} data-testid="nco-revenue" />
        </div>
        {err && <div className="text-xs text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="nco-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

function CompanyDetail({ cid, onChange }) {
  const [d, setD] = useState(null);
  useEffect(() => { lccCompany(cid).then(setD).catch(() => setD(null)); }, [cid]);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const co = d.company;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`company-detail-${cid}`}>
      <div>
        <h2 className="text-xl font-bold text-slate-900">{co.name}</h2>
        <div className="text-sm text-slate-500 mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
          {co.domain && <span>🌐 {co.domain}</span>}
          {co.website && <a href={co.website} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">website <ExternalLink className="w-3 h-3" /></a>}
          {co.industry && <span>🏢 {co.industry}</span>}
          {co.size && <span>👥 {co.size}</span>}
          {co.revenue && <span>💰 {co.revenue}</span>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-slate-200 p-3"><div className="text-2xl font-bold text-slate-900">{d.contacts.length}</div><div className="text-xs text-slate-500">People</div></div>
        <div className="rounded-lg border border-slate-200 p-3"><div className="text-2xl font-bold text-slate-900">{d.opportunities.length}</div><div className="text-xs text-slate-500">Opportunities</div></div>
        <div className="rounded-lg border border-slate-200 p-3"><div className="text-2xl font-bold text-emerald-600">${(d.lifetime_value || 0).toLocaleString()}</div><div className="text-xs text-slate-500">Lifetime value (won)</div></div>
      </div>
      {d.contacts.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">People ({d.contacts.length})</div>
          <div className="space-y-1">
            {d.contacts.slice(0, 10).map((c) => (
              <div key={c.id} className="text-xs rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between" data-testid={`cd-contact-${c.id}`}>
                <span><strong>{c.name}</strong> <span className="text-slate-400">— {(c.emails || [])[0] || (c.phones || [])[0] || ""}</span></span>
                <span className="text-slate-400 text-[10px]">Last seen {fmtRelative(c.last_seen)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {d.leads.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Leads ({d.leads.length})</div>
          <div className="space-y-1">
            {d.leads.slice(0, 5).map((l) => (
              <div key={l.id} className="text-xs rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between">
                <span className="truncate">{l.source} · {l.requested_service || "no service"}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STAGE_COLORS[l.stage]}`}>{l.stage}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Routing rules
// ============================================================================
function RoutingTab() {
  const [rules, setRules] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [testing, setTesting] = useState(false);
  const load = useCallback(() => { lccRoutingRules().then((r) => setRules(r.rules || [])).catch(() => setRules([])); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (rid) => { if (!window.confirm("Delete this rule?")) return; await lccDeleteRoutingRule(rid); load(); };
  const toggle = async (r) => { await lccUpdateRoutingRule(r.id, { ...r, enabled: !r.enabled }); load(); };
  if (rules === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-4" data-testid="lcc-routing">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-slate-500 flex items-center gap-1 max-w-2xl"><Info className="w-3 h-3" /> Rules run in priority order (lowest first). First match wins — assigns the lead to a person or round-robins across a team, and starts an SLA clock with an automatic task.</div>
        <div className="flex gap-2">
          <button onClick={() => setTesting(true)} data-testid="routing-test-btn" className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Route className="w-4 h-4" /> Test routing</button>
          <button onClick={() => setShowNew(true)} data-testid="routing-new-btn" className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700"><Plus className="w-4 h-4" /> New rule</button>
        </div>
      </div>
      {rules.length === 0 ? <div className="text-sm text-slate-500 py-16 text-center rounded-2xl border border-dashed border-slate-200 bg-white">No routing rules. New leads stay unassigned until you add one.</div> :
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {rules.map((r) => (
            <div key={r.id} className="border-b border-slate-100 last:border-b-0 px-4 py-3 flex items-center gap-3" data-testid={`rule-${r.id}`}>
              <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={r.enabled} onChange={() => toggle(r)} data-testid={`rule-toggle-${r.id}`} className="w-4 h-4" />
              </label>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{r.name} <span className="text-xs text-slate-400 font-normal">· priority {r.priority} · SLA {r.sla_minutes}m</span></div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {(r.conditions || []).length ? (r.conditions.map((c) => `${c.field} ${c.op} ${JSON.stringify(c.value)}`).join(" AND ")) : "any lead"}
                  {" → "}
                  <strong>{r.action?.type === "round_robin" ? `round-robin: ${(r.action.targets || []).join(", ")}` : `assign: ${r.action?.target}`}</strong>
                </div>
              </div>
              <button onClick={() => remove(r.id)} data-testid={`rule-delete-${r.id}`} className="p-1.5 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>}
      {showNew && <NewRuleModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {testing && <TestRoutingModal rules={rules} onClose={() => setTesting(false)} />}
    </div>
  );
}

function NewRuleModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", priority: 100, sla_minutes: 60,
    conditions: [{ field: "service", op: "contains", value: "" }],
    action: { type: "assign", target: "" },
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!f.name.trim()) { setErr("Name required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { ...f, priority: Number(f.priority), sla_minutes: Number(f.sla_minutes), enabled: true };
      if (payload.action.type === "round_robin") {
        payload.action = { type: "round_robin", targets: (payload.action.target || "").split(",").map((s) => s.trim()).filter(Boolean) };
      }
      payload.conditions = payload.conditions.filter((c) => c.field && c.value !== "");
      await lccCreateRoutingRule(payload);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const setCond = (i, k, v) => { const arr = [...f.conditions]; arr[i] = { ...arr[i], [k]: v }; setF({ ...f, conditions: arr }); };
  const addCond = () => setF({ ...f, conditions: [...f.conditions, { field: "source", op: "eq", value: "" }] });
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-rule-modal">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-slate-900">New routing rule</h3><button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button></div>
        <input className={field} placeholder="Rule name (e.g. Kerala tour operators)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="nr-name" />
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-500">Priority (lower = runs first)</label><input type="number" className={field} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="nr-priority" /></div>
          <div><label className="text-xs text-slate-500">SLA (minutes)</label><input type="number" className={field} value={f.sla_minutes} onChange={(e) => setF({ ...f, sla_minutes: e.target.value })} data-testid="nr-sla" /></div>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">Conditions (ALL must match)</div>
          {f.conditions.map((c, i) => (
            <div key={i} className="grid grid-cols-[100px_80px_1fr] gap-1">
              <select className={field} value={c.field} onChange={(e) => setCond(i, "field", e.target.value)} data-testid={`nr-cond-field-${i}`}>
                <option value="source">source</option><option value="service">service</option>
                <option value="urgency">urgency</option><option value="score">score</option>
                <option value="has_email">has_email</option><option value="has_phone">has_phone</option>
                <option value="country">country</option>
              </select>
              <select className={field} value={c.op} onChange={(e) => setCond(i, "op", e.target.value)}>
                <option value="eq">=</option><option value="neq">≠</option>
                <option value="contains">contains</option><option value="gte">≥</option>
                <option value="lte">≤</option>
              </select>
              <input className={field} placeholder="value" value={c.value} onChange={(e) => setCond(i, "value", e.target.value)} data-testid={`nr-cond-val-${i}`} />
            </div>
          ))}
          <button onClick={addCond} className="text-xs text-indigo-600 hover:text-indigo-800">+ add condition</button>
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">Action</div>
          <select className={field} value={f.action.type} onChange={(e) => setF({ ...f, action: { ...f.action, type: e.target.value } })} data-testid="nr-action-type">
            <option value="assign">assign to person</option>
            <option value="round_robin">round-robin across team (comma-separated emails)</option>
          </select>
          <input className={field} placeholder={f.action.type === "round_robin" ? "a@co.com, b@co.com, c@co.com" : "owner@company.com"} value={f.action.target || ""} onChange={(e) => setF({ ...f, action: { ...f.action, target: e.target.value } })} data-testid="nr-action-target" />
        </div>
        {err && <div className="text-xs text-rose-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="nr-save" className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
        </div>
      </div>
    </div>
  );
}

function TestRoutingModal({ rules, onClose }) {
  const [f, setF] = useState({ source: "website", service: "", urgency: "normal", score: 50 });
  const [result, setResult] = useState(null);
  const test = async () => {
    const r = await lccTestRouting({ lead: { ...f, score: { overall: Number(f.score) } }, contact: {} });
    setResult(r);
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="test-routing-modal">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-slate-900">Test routing</h3><button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button></div>
        <div className="text-xs text-slate-500">Simulate a hypothetical lead — see which rule fires.</div>
        <input className={field} placeholder="Source (website, ai-receptionist…)" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="tr-source" />
        <input className={field} placeholder="Requested service" value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="tr-service" />
        <div className="grid grid-cols-2 gap-2">
          <select className={field} value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })} data-testid="tr-urgency"><option>low</option><option>normal</option><option>high</option></select>
          <input type="number" className={field} placeholder="Score" value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} data-testid="tr-score" />
        </div>
        <button onClick={test} data-testid="tr-run" className="w-full inline-flex justify-center items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"><Route className="w-4 h-4" /> Run test</button>
        {result && (
          <div className="rounded-lg border p-3 text-sm" data-testid="tr-result">
            {result.matched ? (
              <>
                <div className="text-emerald-700 font-semibold">✓ Matched: {result.rule.name}</div>
                <div className="text-xs text-slate-600 mt-1">→ assign to <strong>{result.assignee}</strong> · SLA {result.rule.sla_minutes}m</div>
              </>
            ) : <div className="text-amber-700">No rule matched — this lead would stay unassigned.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Audit log
// ============================================================================
function AuditTab() {
  const [rows, setRows] = useState(null);
  useEffect(() => { lccAudit({ limit: 200 }).then((r) => setRows(r.events || [])).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (rows.length === 0) return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center" data-testid="lcc-audit-empty">
      <History className="w-8 h-8 mx-auto text-slate-300 mb-2" />
      <p className="text-sm text-slate-500">No audit events yet.</p>
      <p className="text-xs text-slate-400 mt-1">Field changes on contacts, leads and opportunities are logged here.</p>
    </div>
  );
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="lcc-audit">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Who</th><th className="text-left px-3 py-2">Entity</th><th className="text-left px-3 py-2">Field</th><th className="text-left px-3 py-2">Change</th></tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-t border-slate-100" data-testid={`audit-${e.id}`}>
              <td className="px-3 py-2 text-xs text-slate-500">{fmtRelative(e.at)}</td>
              <td className="px-3 py-2 text-xs">{e.actor_id} <span className="text-slate-400">({e.actor_kind})</span></td>
              <td className="px-3 py-2 text-xs">{e.entity_type} <span className="text-slate-400">{e.entity_id?.slice(0, 8)}</span></td>
              <td className="px-3 py-2 text-xs font-mono">{e.field}</td>
              <td className="px-3 py-2 text-xs"><span className="text-rose-600">{JSON.stringify(e.old)?.slice(0, 40)}</span> → <span className="text-emerald-600">{JSON.stringify(e.new)?.slice(0, 40)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
